import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import Logo from '../components/ui/Logo';
import Footer from '../components/organisms/Footer';

const PrivacyPage = () => {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.6
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Header */}
      <motion.header 
        className="bg-white shadow-sm border-b"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="text-neutral-600 hover:text-neutral-900"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
              <Logo variant="full" size="medium" className="hidden sm:block" />
            </div>
          </div>
        </div>
      </motion.header>

      <motion.main
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        {/* Effective Date and Intro */}
        <motion.section variants={itemVariants} className="mb-12">
          <Badge variant="secondary" className="mb-4">Legal Hub</Badge>
          <Card className="bg-white shadow-sm">
            <CardContent className="pt-6">
              <h1 className="text-3xl font-heading font-bold text-neutral-900 mb-4">CROWBLOCK Privacy Policy</h1>
              <p className="text-neutral-600 mb-2">Effective Date: 01-August-2025</p>
              <p className="text-lg text-neutral-800 mb-4">
                At CROWBLOCK, we value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your data when you use our crypto-powered crowdfunding platform.
              </p>
            </CardContent>
          </Card>
        </motion.section>

        {/* Privacy Policy */}
        <motion.section variants={itemVariants}>
          <Card className="bg-white shadow-sm">
            <CardHeader>
              <CardTitle className="text-2xl font-heading font-semibold text-neutral-900">2. Privacy Policy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Information We Collect</h3>
                <ul className="list-disc list-inside space-y-1 text-neutral-600">
                  <li><strong>Account Data:</strong> Name, email, and wallet address.</li>
                  <li><strong>Transaction Data:</strong> Contributions and campaign details.</li>
                  <li><strong>Usage Data:</strong> Device, browser, and activity on the platform.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">How We Use Information</h3>
                <ul className="list-disc list-inside space-y-1 text-neutral-600">
                  <li>To operate and improve the platform.</li>
                  <li>To process contributions and campaigns.</li>
                  <li>To comply with legal and regulatory requirements.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Data Sharing</h3>
                <ul className="list-disc list-inside space-y-1 text-neutral-600">
                  <li>With service providers (payment processors, hosting, analytics).</li>
                  <li>With legal authorities where required by law.</li>
                  <li>We do not sell your personal data.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Data Security</h3>
                <ul className="list-disc list-inside space-y-1 text-neutral-600">
                  <li>We use industry-standard measures to secure your information.</li>
                  <li>Users are responsible for protecting their wallets and private keys.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">User Rights</h3>
                <ul className="list-disc list-inside space-y-1 text-neutral-600">
                  <li>Depending on jurisdiction, you may request access, updates, or deletion of your data.</li>
                  <li>You may opt out of non-essential communications.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Cookies & Tracking</h3>
                <ul className="list-disc list-inside space-y-1 text-neutral-600">
                  <li>We may use cookies to improve your experience.</li>
                  <li>You can adjust browser settings to manage cookies.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Children's Privacy</h3>
                <p className="text-neutral-600">The platform is not for individuals under 18.</p>
                <p className="text-neutral-600">We do not knowingly collect information from children.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-neutral-800 mb-2">Changes to Policy</h3>
                <p className="text-neutral-600">Updates will be posted with a new effective date.</p>
              </div>
            </CardContent>
          </Card>
        </motion.section>
      </motion.main>

      <Footer variant="minimal" />
    </div>
  );
};

export default PrivacyPage;